import React, { useContext, useEffect, useState } from "react";

import { useAppStore } from "../../../../context/store";
import { Dialog, Portal, Select, CloseButton, Button, createListCollection, Field, Input, Box, HStack, Textarea, Spinner } from "@chakra-ui/react";
import { LuPlus, LuSquarePen, LuText, LuUsers } from "react-icons/lu";
import { serviceAddEditTeam, serviceGetTeam } from "../../../../api/services/team";
import { floatingStyles, showToast } from "../../../../utilies/Utility";
import Type from "../../../../utilies/Types";

export const UsersAndTeamManagementNewTeamDialog = () => {
  // state
  const showNewTeamDialog = useAppStore((s) => s.showNewTeamDialog);
  const users = useAppStore((s) => s.users);
  const userType = useAppStore((s) => s.userType);
  const userData = useAppStore((s) => s.userData);
  const selectTeamForEdit = useAppStore((s) => s.selectTeamForEdit);

  // setters
  const setShowNewTeamDialog = useAppStore.getState().setShowNewTeamDialog;
  const setTeams = useAppStore.getState().setTeams;
  const setSelectTeamForEdit = useAppStore.getState().setSelectTeamForEdit;

  const [contributorsOptions, setContributorsOptions] = useState(createListCollection({ items: [] }));
  const [contributors, setContributors] = useState([]);
  const [admin, setAdmin] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [teamId, setTeamId] = useState("");

  const [isProgress, setIsProgress] = useState(false);

  useEffect(() => {
    if (selectTeamForEdit && showNewTeamDialog) {
      setAdmin(selectTeamForEdit.team_admin);
      setDescription(selectTeamForEdit.description);
      setTitle(selectTeamForEdit.team_name);
      setContributors(selectTeamForEdit.members);
      setTeamId(selectTeamForEdit.id);
    }
    if (!showNewTeamDialog) {
      setSelectTeamForEdit(null);
    }
  }, [showNewTeamDialog]);

  useEffect(() => {
    if (users) {
      const newContributors = createListCollection({
        items: Object.values(users)
          .sort((a, b) => a.username.localeCompare(b.username))
          .map((user) => ({
            label: (`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username) + " (" + user.username + ")",
            value: user.username,
          })),
      });

      setContributorsOptions(newContributors);
    }
  }, [users]);

  const addEditTeam = () => {
    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        team_name: title,
        description: description,
        team_admin: admin,
        team_members: contributors,
      }),
    );

    if (teamId) {
      formData.append("team_id", teamId);
    }

    setIsProgress(true);
    serviceAddEditTeam(formData)
      .then((res) => {
        setIsProgress(false);
        if (res.data.status == "failed") {
          showToast(res.data.message, Type.NOTIFICATION.ERROR);
        } else {
          showToast(res.data.message, Type.NOTIFICATION.SUCCESS);
          setShowNewTeamDialog(false);
          getTeamFromServer();
        }
      })
      .catch((err) => {
        console.error("Error", err);
        showToast(err.message, Type.NOTIFICATION.ERROR);
        setIsProgress(false);
      });
  };

  const getTeamFromServer = () => {
    const formData = new FormData();
    if (userType.includes(Type.ROLE.ADMIN)) {
      formData.append("team_admin", userData?.user?.username);
    }

    serviceGetTeam(formData)
      .then((res) => {
        if (res.data.status == "failed") {
          showToast(res.data.message, Type.NOTIFICATION.ERROR);
        } else {
          setTeams(res.data.teams);
        }
      })
      .catch((err) => {
        console.error("Error", err);
      });
  };

  return (
    <Dialog.Root lazyMount open={showNewTeamDialog} closeOnInteractOutside={false} onOpenChange={(e) => setShowNewTeamDialog(e.open)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{selectTeamForEdit ? "Edit" : "New"} Team</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Field.Root>
                <Box pos="relative" w="full">
                  <Input
                    className="peer"
                    placeholder=""
                    type="text"
                    value={title}
                    onChange={(v) => {
                      setTitle(v.target.value);
                    }}
                  />
                  <Field.Label css={floatingStyles}>
                    <LuText /> Team Name
                  </Field.Label>
                </Box>
              </Field.Root>

              <Field.Root mt={5}>
                <Box pos="relative" w="full">
                  <Textarea
                    rows={5}
                    className="peer"
                    placeholder=""
                    type="text"
                    value={description}
                    onChange={(v) => {
                      setDescription(v.target.value);
                    }}
                  />
                  <Field.Label css={floatingStyles}>
                    <LuText /> Description
                  </Field.Label>
                </Box>
              </Field.Root>

              <Select.Root
                collection={contributorsOptions}
                size="sm"
                width="full"
                mt={3}
                mb={3}
                value={[admin]}
                onValueChange={(val) => {
                  setAdmin(val.value[0]);
                }}
              >
                <Select.HiddenSelect />
                <Select.Label>
                  <HStack>
                    <LuUsers /> Team Admin
                  </HStack>
                </Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Contributors" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content>
                    {contributorsOptions.items.map((framework) => (
                      <Select.Item item={framework} key={framework.value}>
                        {framework.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>

              <Select.Root
                multiple
                collection={contributorsOptions}
                size="sm"
                width="full"
                mt={3}
                mb={3}
                value={contributors}
                onValueChange={(val) => {
                  setContributors(val.value);
                }}
              >
                <Select.HiddenSelect />
                <Select.Label>
                  <HStack>
                    <LuUsers /> Contributors
                  </HStack>
                </Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Contributors" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content>
                    {contributorsOptions.items.map((framework) => (
                      <Select.Item item={framework} key={framework.value}>
                        {framework.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button colorPalette={"green"} onClick={addEditTeam} disabled={isProgress}>
                {isProgress ? (
                  <Spinner size={"sm"} />
                ) : selectTeamForEdit ? (
                  <>
                    <LuSquarePen /> Edit
                  </>
                ) : (
                  <>
                    <LuPlus /> Add
                  </>
                )}
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default UsersAndTeamManagementNewTeamDialog;
